import React from 'react';
import type { CountryCode } from '../../config/countries';
import { aboutThisData } from '../../config/products';
import type { DataProductId, MeasureKey } from '../../config/products';
import type { DataWindowState } from '../../hooks/useDataWindow';
import { formatDayLabel } from '../../utils/dateWindow';

export interface AboutThisDataProps {
  country: CountryCode;
  product: DataProductId;
  measure?: MeasureKey;
  /** Delivered days, ascending YYYY-MM-DD. Leave out when the page has no data window. */
  dates?: readonly string[];
}

/**
 * The delivered days to show in the panel, or undefined while they are not known yet (still
 * loading, no access, an error), so the panel never claims "No dates delivered yet." too early.
 */
export function deliveredDays(dw: Pick<DataWindowState, 'status' | 'dates'>): readonly string[] | undefined {
  return dw.status === 'ready' || dw.status === 'no-dates' ? dw.dates : undefined;
}

function datesLine(dates: readonly string[]): string {
  if (dates.length === 0) return 'No dates delivered yet.';
  if (dates.length === 1) return `Dates covered: ${formatDayLabel(dates[0])}.`;
  return `Dates covered: ${dates.length} days, ${formatDayLabel(dates[0])} to ${formatDayLabel(dates[dates.length - 1])}.`;
}

/** A closed-by-default panel: where the data comes from, which days exist and what not to do with it. */
const AboutThisData: React.FC<AboutThisDataProps> = ({ country, product, measure, dates }) => {
  const about = aboutThisData(country, product, measure);
  if (!about) return null;
  return (
    <div className="collapse collapse-arrow bg-base-200">
      <input type="checkbox" aria-label="About this data" />
      <div className="collapse-title font-medium text-ink">About this data</div>
      <div className="collapse-content space-y-2 text-sm">
        <p className="text-ink">
          <span className="font-medium">Source: {about.sourceName}.</span>{' '}
          <span className="text-ink-muted">{about.summary}</span>
        </p>
        {dates ? <p className="text-ink-muted">{datesLine(dates)}</p> : null}
        {about.caveats.length > 0 ? (
          <ul className="list-disc pl-5 text-ink-muted">
            {about.caveats.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default AboutThisData;
